import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { Strings } from "@lib/Strings"
import { Contact, labelForContact } from "@model/contacts/Contact"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { ServiceCode } from "@model/serviceRequests/ServiceRequest"
import { Site } from "@model/site/Site"
import { UserData } from "@model/user/User"
import { WorkOrder } from "@model/workOrders/WorkOrder"
import { CompanyBloc, CompanyState } from "@state/company/CompanyBloc"
import { UserBloc, UserState } from "@state/user/UserBloc"
import {
    WorkHistoryEntry,
    WorkHistoryOverlayBloc,
    WorkHistoryOverlayEffect,
    WorkHistoryOverlayState,
} from "@state/workOrders/WorkHistoryOverlayBloc"

type Dependencies = [WorkHistoryOverlayState, CompanyState, UserState]

export type StateSelection = WorkHistoryOverlayState & {
    /** Entries left after applying the search box, in the order they are listed. */
    filteredEntries: WorkHistoryEntry[]
    selectedEntry: WorkHistoryEntry | null
    isLoading: boolean
    hasDataFetchError: boolean
    contacts: Contact[]
    sites: Site[]
    serviceCodes: Record<number, ServiceCode>
}

/**
 * Action Item #6766: read-only Work History Overlay.
 *
 * Every handler here either changes the search text or the selected work order - nothing in the
 * overlay may modify a work order, a service item or an attachment.
 */
export class WorkHistoryOverlayViewModel extends BlocCoordinator<Dependencies, StateSelection> {
    constructor(
        private workHistoryOverlayBloc: WorkHistoryOverlayBloc,
        private companyBloc: CompanyBloc,
        private userBloc: UserBloc
    ) {
        super([workHistoryOverlayBloc, companyBloc, userBloc])
    }

    protected transform = ([overlayState, companyState]: Dependencies): StateSelection => {
        const filteredEntries = overlayState.entries.filter((entry) =>
            this.entryMatchesSearch(entry, overlayState.searchText, companyState.contacts)
        )

        return {
            ...overlayState,
            filteredEntries: filteredEntries,
            selectedEntry:
                filteredEntries.find((it) => it.workOrder.id === overlayState.selectedWorkOrderId) ?? null,
            isLoading: overlayState.effectStatus[WorkHistoryOverlayEffect.Fetch].isBusy(),
            hasDataFetchError: overlayState.effectStatus[WorkHistoryOverlayEffect.Fetch].isError(),
            contacts: companyState.contacts,
            sites: companyState.allSites,
            serviceCodes: companyState.serviceCodes,
        }
    }

    onCloseClicked = () => this.workHistoryOverlayBloc.hide()

    searchTextChanged = (searchText: string) => this.workHistoryOverlayBloc.searchTextChanged(searchText)

    workOrderSelected = (workOrderId: number) => this.workHistoryOverlayBloc.workOrderSelected(workOrderId)

    retryClicked = () => {
        if (this.state.assetId != null) this.workHistoryOverlayBloc.fetchHistory(this.state.assetId)
    }

    /**
     * Search targets (Action Item #6766): work order number, complaint/instructions, created by,
     * assigned to, service item number, service item name, service item work performed by and
     * service item mechanic's notes.
     *
     * Note: "Work Performed By" is stored on the work order, not on the individual service item,
     * so every service item of a work order matches on that work order's performers.
     */
    private entryMatchesSearch = (entry: WorkHistoryEntry, searchText: string, contacts: Contact[]): boolean => {
        const term = searchText.trim().toLowerCase()
        if (term.length === 0) return true

        const workOrder = entry.workOrder
        const haystack = [
            workOrder.number,
            workOrder.specialInstructions,
            this.createdByLabel(workOrder, contacts),
            this.contactLabel(workOrder.assignedTo, contacts),
            ...(workOrder.workPerformedById ?? []).map((id) => this.contactLabel(id, contacts)),
            ...entry.serviceItems.flatMap((item) => [this.serviceItemNumber(item), item.name, item.notes]),
        ]

        return haystack.some((value) => (value ?? "").toLowerCase().includes(term))
    }

    /** Service item number as it is rendered on the service item card. */
    serviceItemNumber = (item: ServiceItemForm): string =>
        item.id ? item.id.toString().padStart(6, "0") : "PENDING"

    serviceCodeLabel = (item: ServiceItemForm): string =>
        item.serviceCode ? this.state.serviceCodes[item.serviceCode.id]?.code ?? "" : ""

    getContactById = (id: number | null): Contact | null => {
        if (!id) return null
        return this.state.contacts.find((it) => it.id === id) ?? null
    }

    contactLabel = (id: number | null, contacts: Contact[] = this.state.contacts): string => {
        if (!id) return ""
        const contact = contacts.find((it) => it.id === id)
        return contact ? labelForContact(contact, this.userBloc.state.user) : ""
    }

    /**
     * Work orders raised by the automation have no creator, matching the Create Work Order form.
     */
    createdByLabel = (workOrder: WorkOrder, contacts: Contact[] = this.state.contacts): string => {
        if (!workOrder.createdByID && !workOrder.createdByUserData) return "Work Order Automation"

        const fromContacts = this.contactLabel(workOrder.createdByUserData?.personId ?? workOrder.createdByID, contacts)
        if (fromContacts.length > 0) return fromContacts

        return userDataDisplayName(workOrder.createdByUserData)
    }

    createdByContact = (workOrder: WorkOrder): Contact | null =>
        this.getContactById(workOrder.createdByUserData?.personId ?? workOrder.createdByID)

    workPerformedByLabel = (workOrder: WorkOrder): string =>
        (workOrder.workPerformedById ?? [])
            .map((id) => this.contactLabel(id))
            .filter((label) => label.length > 0)
            .join(", ")

    siteName = (workOrder: WorkOrder): string =>
        this.state.sites.find((it) => it.id === workOrder.siteId)?.name ?? ""

    formatDateTime = (date: Date | null): string => (date ? Strings.formatDateTime(date) : "")

    formatDate = (date: Date | null): string => (date ? Strings.formatDate(date) : "")

    formatMoney = (value: number): string => `$${value.toFixed(2)}`
}

function userDataDisplayName(userData: UserData | null | undefined): string {
    if (!userData) return ""
    return `${userData.firstName ?? ""} ${userData.lastName ?? ""}`.trim()
}
